import React, { useState } from 'react';
import axios from '../api/axiosConfig';

const TIPOS_DOC = [
  ['CC', 'cédula de ciudadanía'], ['NIT', 'nit'], ['CE', 'cédula de extranjería'],
  ['PP', 'pasaporte'], ['PPT', 'permiso de permanencia temporal (PPT)'], ['TI', 'tarjeta de identidad']
];
const EXT_OK = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
// Documentos obligatorios (nombre del campo que recibe el servidor)
const DOCUMENTOS = [
  { campo: 'FUN', etiqueta: 'FUN' },
  { campo: 'Contrato_Mandato', etiqueta: 'CONTRATO DE MANDATO' },
  { campo: 'Cedula', etiqueta: 'COPIA DE LA CÉDULA' },
  { campo: 'Carta_Solicitud', etiqueta: 'CARTA DE SOLICITUD DIRIGIDA AL ORGANISMO DE TRÁNSITO' }
];

const Estado = ({ ok, children }) => (
  <div style={{ color: ok ? '#15803d' : '#b91c1c', fontWeight: 600 }}>{ok ? '✅' : '❌'} {children}</div>
);

const FormularioDuplicadoLicencia = ({ onClose, onSuccess }) => {
  const [placa, setPlaca] = useState('');
  const [consultando, setConsultando] = useState(false);
  const [info, setInfo] = useState(null);
  const [errorPlaca, setErrorPlaca] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [buscandoProp, setBuscandoProp] = useState(false);
  const [archivos, setArchivos] = useState({});
  const [f, setF] = useState({
    propietario: '', tipo_documento: 'CC', documento: '', correo: '', telefono: '', direccion: '',
    estado_persona: null, tiene_multas: false,
    representante_legal_nombre: '', representante_legal_documento: '', observaciones: ''
  });

  const set = (name, value) => setF(prev => ({ ...prev, [name]: value }));
  const up = (e) => set(e.target.name, e.target.value.toUpperCase());

  const consultarPlaca = async () => {
    const p = placa.trim().toUpperCase();
    if (p.length < 5) { alert('⚠️ Ingrese la placa del vehículo'); return; }
    setConsultando(true); setErrorPlaca(''); setInfo(null);
    try {
      const res = await axios.get('/api/tramites/duplicado-licencia/lookup', { params: { placa: p, forzar: true }, timeout: 120000 });
      setInfo(res.data);
    } catch (err) {
      setErrorPlaca(err.response?.data?.message || 'No se pudo consultar la placa');
    } finally {
      setConsultando(false);
    }
  };

  const buscarPropietario = async () => {
    if (!f.documento) { alert('⚠️ Ingrese el documento del propietario'); return; }
    setBuscandoProp(true);
    try {
      const res = await axios.get(`/api/tramites/owner/${f.documento}?tipo_documento_id=${f.tipo_documento}`);
      if (res.data) {
        setF(prev => ({
          ...prev,
          propietario: res.data.propietario || prev.propietario,
          correo: res.data.correo || prev.correo, telefono: res.data.telefono || prev.telefono, direccion: res.data.direccion || prev.direccion,
          estado_persona: res.data.estado_persona || null, tiene_multas: Boolean(res.data.tiene_multas)
        }));
        if (res.data.tiene_multas) alert('🚨 El propietario presenta multas en SIMIT. Se generará una novedad para el concesionario.');
      }
    } catch (err) {
      alert(err.response?.status === 404 ? 'ℹ️ Propietario no encontrado. Ingrese los datos manualmente.' : '❌ Error al consultar el propietario');
    } finally { setBuscandoProp(false); }
  };

  const elegirArchivo = (campo, e) => {
    const file = e.target.files[0];
    if (!file) { setArchivos(a => ({ ...a, [campo]: null })); return; }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!EXT_OK.includes(ext)) { alert(`❌ "${file.name}" no es PDF ni imagen.`); e.target.value = ''; return; }
    if (file.size > 6 * 1024 * 1024) { alert(`❌ "${file.name}" supera los 6MB`); e.target.value = ''; return; }
    setArchivos(a => ({ ...a, [campo]: file }));
  };

  const enviar = async () => {
    if (!info || !info.puede_crear) { alert('⚠️ Consulte primero una placa que cumpla los requisitos'); return; }
    if (!f.propietario.trim() || !f.documento.trim()) { alert('⚠️ Complete los datos del propietario'); return; }
    const faltan = DOCUMENTOS.filter(d => !archivos[d.campo]).map(d => d.etiqueta);
    if (faltan.length) { alert(`⚠️ Faltan documentos obligatorios:\n• ${faltan.join('\n• ')}`); return; }
    const total = DOCUMENTOS.reduce((a, d) => a + (archivos[d.campo]?.size || 0), 0);
    if (total > 15 * 1024 * 1024) { alert('❌ La suma de los archivos no puede superar 15MB'); return; }

    setEnviando(true);
    const data = new FormData();
    data.append('transito_id', info.transito.id);
    data.append('tipo_tramite_id', info.tipo_tramite_id);
    data.append('placa_referencia', info.placa);
    data.append('datos_adicionales', JSON.stringify({ ...f, placa_referencia: info.placa }));
    DOCUMENTOS.forEach(d => data.append(d.campo, archivos[d.campo]));
    try {
      await axios.post('/api/tramites', data, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      alert('✅ Duplicado de licencia de tránsito radicado correctamente');
      onSuccess(); onClose();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.message || 'No se pudo crear el trámite'));
    } finally { setEnviando(false); }
  };

  const req = <span style={{ color: 'red' }}>*</span>;
  const v = info?.vehiculo || {};

  return (
    <div>
      <div className="form-section">
        <div className="form-section-header">
          <div className="form-section-icon">🚗</div>
          <h4 className="form-section-title">PLACA DEL DUPLICADO DE LICENCIA DE TRÁNSITO</h4>
        </div>
        <small style={{ display: 'block', margin: '8px 0' }}>
          Se consulta el RUNT: el vehículo debe tener SOAT y revisión técnico-mecánica activos, y la placa debe tener un CUPL de duplicado de licencia registrado en el sistema.
        </small>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input type="text" value={placa} maxLength={7} placeholder="PLACA"
            onChange={(e) => { setPlaca(e.target.value.toUpperCase()); setInfo(null); setErrorPlaca(''); }}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), consultarPlaca())}
            style={{ flex: 1, textTransform: 'uppercase', fontWeight: 'bold' }} />
          <button type="button" className="btn-action btn-modules" onClick={consultarPlaca} disabled={consultando}
            style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}>
            {consultando ? '⏳ CONSULTANDO...' : 'CONSULTAR'}
          </button>
        </div>
        {consultando && <small style={{ display: 'block', marginTop: '6px' }}>Consultando el RUNT, puede tardar hasta 30 segundos...</small>}
        {errorPlaca && <div className="error-banner" style={{ marginTop: '10px' }}>{errorPlaca}</div>}
        {info && (
          <div style={{ marginTop: '12px', padding: '12px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--secondary-soft)', fontSize: '13px', display: 'grid', gap: '6px' }}>
            <div><b>Vehículo:</b> {[v.marca, v.linea, v.modelo, v.color, v.clase].filter(Boolean).join(' · ') || 'sin datos'}</div>
            <Estado ok={info.soat?.ok}>
              SOAT {info.soat?.ok ? `vigente${info.soat.fecha_fin ? ' hasta ' + info.soat.fecha_fin : ''}` : 'NO vigente'}
            </Estado>
            <Estado ok={info.rtm?.ok}>
              Revisión técnico-mecánica {info.rtm?.ok ? (info.rtm.estado === 'no_aplica' ? 'no aplica (vehículo nuevo)' : 'vigente') : 'NO vigente'}
            </Estado>
            <Estado ok={!!info.cupl}>
              {info.cupl ? `CUPL ${info.cupl.numero} registrado (${info.cupl.pagado ? 'pagado' : 'pendiente de pago'})` : 'Sin CUPL de duplicado registrado'}
            </Estado>
            {info.transito && <div><b>Organismo de tránsito:</b> {info.transito.nombre}</div>}
            {(info.bloqueos || []).map((b, i) => (
              <div key={i} className="error-banner" style={{ marginTop: '4px' }}>{b}</div>
            ))}
          </div>
        )}
      </div>

      {info && info.puede_crear && (
        <>
          <div className="form-section">
            <div className="form-section-header">
              <div className="form-section-icon">👤</div>
              <h4 className="form-section-title">DATOS DEL PROPIETARIO</h4>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginTop: '12px' }}>
              <div className="form-group">
                <label>TIPO DOC. {req}</label>
                <select value={f.tipo_documento} onChange={(e) => set('tipo_documento', e.target.value)}>
                  {TIPOS_DOC.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>NRO DE DOCUMENTO {req}</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input type="text" name="documento" value={f.documento} onChange={up} style={{ flex: 1 }} />
                  <button type="button" className="btn-action btn-modules" onClick={buscarPropietario} disabled={buscandoProp}
                    style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}>{buscandoProp ? '⏳' : 'CONSULTAR'}</button>
                </div>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group"><label>NOMBRE / RAZÓN SOCIAL {req}</label>
                <input type="text" name="propietario" value={f.propietario} onChange={up} /></div>
              <div className="form-group"><label>CORREO</label>
                <input type="email" value={f.correo} onChange={(e) => set('correo', e.target.value)} /></div>
              <div className="form-group"><label>TELÉFONO</label>
                <input type="text" name="telefono" value={f.telefono} onChange={up} /></div>
              <div className="form-group"><label>DIRECCIÓN</label>
                <input type="text" name="direccion" value={f.direccion} onChange={up} /></div>
            </div>
            {f.tipo_documento === 'NIT' && (
              <div className="form-grid">
                <div className="form-group"><label>REPRESENTANTE LEGAL</label>
                  <input type="text" name="representante_legal_nombre" value={f.representante_legal_nombre} onChange={up} /></div>
                <div className="form-group"><label>DOC. REPRESENTANTE LEGAL</label>
                  <input type="text" name="representante_legal_documento" value={f.representante_legal_documento} onChange={up} /></div>
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <div className="form-section-icon">📎</div>
              <h4 className="form-section-title">DOCUMENTOS</h4>
            </div>
            {DOCUMENTOS.map(d => (
              <div className="form-group" key={d.campo}>
                <label>{d.etiqueta} {req}</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => elegirArchivo(d.campo, e)} />
                {archivos[d.campo] && <small style={{ color: '#16a34a' }}>✓ {archivos[d.campo].name}</small>}
              </div>
            ))}
            <div className="form-group">
              <label>OBSERVACIONES</label>
              <textarea name="observaciones" value={f.observaciones} onChange={up} rows={2} />
            </div>
          </div>
        </>
      )}

      <div className="modal-actions" style={{ padding: '0 20px 20px 20px' }}>
        <button type="button" onClick={onClose} className="btn-secondary" disabled={enviando}>cancelar</button>
        <button type="button" onClick={enviar} className="btn-primary" disabled={enviando || !info || !info.puede_crear}
          style={{ padding: '10px 25px', fontSize: '13px', fontWeight: '800' }}>
          {enviando ? '🚀 ENVIANDO...' : '🚀 RADICAR DUPLICADO'}
        </button>
      </div>
    </div>
  );
};

export default FormularioDuplicadoLicencia;
